import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, Globe } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../ui/button';
import logoImg from '../../assets/logo-80.png';

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { language, toggleLanguage, t } = useLanguage();
  const location = useLocation();
  
  // Pages with dark backgrounds
  const darkPages = [];
  const isDarkPage = darkPages.includes(location.pathname);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '/', label: t('nav.home') },
    { href: '/solutions', label: t('nav.solutions') },
    { href: '/portfolio', label: t('nav.portfolio') },
    { href: '/partner', label: t('nav.partner') },
    { href: '/downloads', label: 'Downloads' },
    { href: '/faq', label: t('nav.faq') },
    { href: '/about', label: t('nav.about') },
    { href: '/contact', label: t('nav.contact') },
  ];

  // Helper: resolve color based on scroll state and dark page
  const textColor = (scrolledClass, darkClass, defaultClass) =>
    scrolled ? scrolledClass : isDarkPage ? darkClass : defaultClass;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        textColor('bg-white/95 backdrop-blur-md shadow-sm', 'bg-slate-900/95 backdrop-blur-md', 'bg-transparent')
      }`}
      data-testid="navbar"
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3" data-testid="logo-link">
            <img 
              src={logoImg}
              alt="e-Matrix"
              className="w-10 h-10 rounded-full object-cover"
              width="40"
              height="40"
            />
            <span className={`font-semibold text-lg hidden sm:block ${
              textColor('text-[#0D2440]', 'text-white', 'text-[#0D2440]')
            }`}>
              e-Matrix
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-sm font-medium transition-colors hover:text-[#2E5E99] ${
                  location.pathname === link.href
                    ? 'text-[#2E5E99]'
                    : textColor('text-[#0D2440]', 'text-white/90 hover:text-white', 'text-[#0D2440]')
                }`}
                data-testid={`nav-link-${link.href.replace('/', '') || 'home'}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Desktop CTAs */}
          <div className="hidden lg:flex items-center space-x-4">
            {/* Language Switcher */}
            <button
              onClick={toggleLanguage}
              className={`flex items-center space-x-1 text-sm font-medium transition-colors ${
                textColor('text-[#5A7A9B] hover:text-[#0D2440]', 'text-white/80 hover:text-white', 'text-[#5A7A9B] hover:text-[#0D2440]')
              }`}
              data-testid="language-switcher"
            >
              <Globe className="w-4 h-4" />
              <span>{language.toUpperCase()}</span>
            </button>

            <Link to="/booking" data-testid="cta-demo">
              <Button className="bg-[#2E5E99] hover:bg-[#1E4F85] text-white">
                {t('nav.bookDemo')}
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex lg:hidden items-center space-x-4">
            <button
              onClick={toggleLanguage}
              className={`flex items-center space-x-1 text-sm font-medium ${
                textColor('text-[#5A7A9B]', 'text-white/80', 'text-[#5A7A9B]')
              }`}
              data-testid="mobile-language-switcher"
            >
              <Globe className="w-4 h-4" />
              <span>{language.toUpperCase()}</span>
            </button>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`p-2 ${
                textColor('text-[#0D2440]', 'text-white', 'text-[#0D2440]')
              }`}
              data-testid="mobile-menu-toggle"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden overflow-hidden bg-white border-t"
              data-testid="mobile-menu"
            >
              <div className="py-4 space-y-2">
                {navLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setIsOpen(false)}
                    className={`block px-4 py-3 text-base font-medium transition-colors ${
                      location.pathname === link.href
                        ? 'text-[#2E5E99] bg-[#E7F0FA]'
                        : 'text-[#0D2440] hover:bg-[#E7F0FA]'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
                <div className="px-4 pt-4 space-y-2 border-t">
                  <Link to="/booking" onClick={() => setIsOpen(false)}>
                    <Button className="w-full bg-[#2E5E99] text-white">
                      {t('nav.bookDemo')}
                    </Button>
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
};
